import { useEffect, useRef } from 'react';
import { Sample } from '../../@types/samples/types';
import { useAppContext } from '../../providers/AppContext';
import styles from './SampleTabBar.module.css';

export type SampleTabBarProps = {
  sample: Sample | null;
  recentSamples?: Sample[];
  sampleCount: number | null;
  onOpen: () => void;
  onSelectRecent?: (sample: Sample) => void;
  onCloseTab?: (sample: Sample) => void;
  wellColorMap?: Record<string, string>;
};

const SHORTCUT = '⌘K';

const SampleTabBar = ({
  sample,
  recentSamples,
  sampleCount,
  onOpen,
  onSelectRecent,
  onCloseTab,
}: SampleTabBarProps) => {
  const { data: { wells } } = useAppContext();

  const allSamples = [...(recentSamples ?? [])];
  if (sample && !allSamples.some((s) => s.uid === sample.uid)) {
    allSamples.push(sample);
  }

  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  useEffect(() => {
    if (!sample?.uid) return;
    const el = tabRefs.current.get(sample.uid);
    el?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
  }, [sample?.uid, allSamples.length]);

  const dotColor = (wellUid?: string) => {
    const well = wellUid ? wells.find((w) => w.uid === wellUid) : undefined;
    return well?.color || 'var(--ds-color-primary)';
  };

  const handleClose = (e: React.MouseEvent, s: Sample) => {
    e.stopPropagation();
    onCloseTab?.(s);
  };

  return (
    <div className={styles.sampleBar}>
      <div className={styles.tabsScroller}>
        {allSamples.map((s) => {
          const isActive = s.uid === sample?.uid;
          return (
            <button
              key={s.uid}
              ref={(el) => {
                if (el) tabRefs.current.set(s.uid, el);
                else tabRefs.current.delete(s.uid);
              }}
              className={isActive ? styles.sampleTab : styles.sampleTabRecent}
              onClick={isActive ? onOpen : () => onSelectRecent?.(s)}
              title={isActive ? 'Click to change sample' : `Load ${s.sample_code}`}
              {...(isActive ? { 'data-tour': 'page-header' } : {})}
            >
              <span
                className={styles.sampleTabDot}
                style={{ background: dotColor(s.well_uid) }}
                aria-hidden="true"
              />
              <span className={styles.sampleTabLabel}>{s.sample_code}</span>
              {onCloseTab && (
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`Close ${s.sample_code}`}
                  className={styles.closeBtn}
                  onClick={(e) => handleClose(e, s)}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  ×
                </span>
              )}
            </button>
          );
        })}
      </div>

      <button className={styles.sampleCountBtn} onClick={onOpen} data-tour="load-sample-btn">
        {sampleCount !== null && sampleCount > 0 && (
          <span className={styles.sampleCountText}>See all {sampleCount} samples</span>
        )}
        <kbd className={styles.kbd}>{SHORTCUT}</kbd>
      </button>
    </div>
  );
};

export { SampleTabBar };
